import { Stack } from "@mui/material";
import { Link, useParams } from "react-router";
import { useMemo } from "react";
import { Typography } from "@platform/design-system";

import { ROUTE_NAMES } from "../../../../constants";
import {
  useBaselineDetailQuery,
  useCarrierMapQuery,
  useCarrierRuleSelectionQuery,
} from "../../../../hooks/useBbe";
import { SummaryField } from "../BonusSummaryDescription";

type ScenarioModelSideBarProps = {
  modelId: string;
};

export const ScenarioModelSideBar: React.FC<ScenarioModelSideBarProps> = ({
  modelId,
}) => {
  const { id: brokerIdParam } = useParams<{
    id: string;
  }>();
  const brokerId = brokerIdParam ?? "";

  const { data: carrierRuleData } = useCarrierRuleSelectionQuery();
  const { data: carrierMapData } = useCarrierMapQuery();
  const { data: baselineDetail } = useBaselineDetailQuery(modelId);
  const carrierBonusSettings = useMemo(
    () =>
      (baselineDetail?.carrierBonusYears ?? [])
        .filter((row) => row.sfCarrierId)
        .map((row) => ({
          rowKey: JSON.stringify([
            row.sfCarrierId,
            row.carrierRuleId,
            row.trancheCode,
            row.bonusYear,
          ]),
          carrierId: row.sfCarrierId,
          carrierRuleId: row.carrierRuleId,
          carrierName:
            carrierMapData?.carriers[row.sfCarrierId] ?? row.sfCarrierId,
          snapshotCarrierRule: carrierRuleData?.find(
            (rule) => rule.carrierRuleId === row.carrierRuleId,
          ),
        }))
        .sort((a, b) => a.carrierName.localeCompare(b.carrierName)),
    [
      baselineDetail?.carrierBonusYears,
      carrierMapData?.carriers,
      carrierRuleData,
    ],
  );

  return (
    <Stack
      data-testid="scenario-model-rules-sidebar"
      direction="column"
      spacing={2}
      p={2}
    >
      <Typography variant="body-medium" color="textSecondary">
        Creating a new model locks rules. To apply updated rules, go to{" "}
        <Link
          style={{ textDecoration: "underline" }}
          to={`/${ROUTE_NAMES.BBE}/${brokerId}/${ROUTE_NAMES.BBE_FORECAST}/${ROUTE_NAMES.BBE_BROKER_SETTINGS}`}
        >
          Carrier Bonus Settings
        </Link>
        , make your changes, then create a new model.
      </Typography>

      {carrierBonusSettings?.map((carrier) => (
        <SummaryField
          key={carrier.rowKey}
          title={carrier.carrierName}
          description={
            carrier.snapshotCarrierRule?.ruleTitle ??
            carrier.carrierRuleId ??
            "No rule selected"
          }
        />
      ))}
    </Stack>
  );
};
